import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { CopyButton } from "../components/CopyButton";
import GatedHorizonCases from "../components/GatedHorizonCases";

// ---------------------------------------------------------------- content

const HEADLINE = "Horizon Europe fit: governed coordination for multi-party innovation";
const SUBHEAD =
  "KATLAS helps consortia coordinate evidence, bounded AI/tool use, reporting and change across organisations — without forcing sensitive data into one central platform.";

const CARDS = [
  {
    n: "01",
    title: "The problem",
    body: "Cluster 4 and Cluster 6 projects increasingly rely on evidence, AI-assisted analysis and reporting across labs, suppliers, recyclers, manufacturers, SMEs, assessors, buyers and auditors. Actions and dependencies can move faster than the governance around them.",
    accent: "blue",
  },
  {
    n: "02",
    title: "What KATLAS provides",
    body: "An operational governance layer using role-based workflow wallets and role-to-role protocols. Evidence and data stay local, under the control of the role that holds them.",
    accent: "blue",
  },
  {
    n: "03",
    title: "How it works",
    body: "Authorised roles make permissioned requests. Purpose, identity, authority and AI/tool boundaries are checked. Each significant action creates a signed receipt showing custody, authority, evidence reference, dependency and outcome.",
    accent: "blue",
  },
  {
    n: "04",
    title: "AI governance",
    body: "AI assists. Roles decide. Receipts prove. KATLAS supports bounded agentic AI and, where apposite, small or specialist language models rather than unnecessary large-model use. This supports privacy, cost control, energy efficiency and practical UK / EU adoption.",
    accent: "blue",
  },
  {
    n: "05",
    title: "Inclusion and supplier access",
    body: "Too much assurance is retrofitted onto fragmented systems, pushing cost onto third- and fourth-tier suppliers. KATLAS provides accessible governance infrastructure so smaller partners can evidence performance, safety, sustainability and change without a dominant platform or payment intermediary owning the orchestration layer.",
    accent: "blue",
  },
  {
    n: "06",
    title: "What KATLAS can lead",
    body: "KATLAS can lead a work package on operational governance, trusted evidence coordination, bounded AI/tool use and project lifecycle reporting. Our devtool / dtool and research-assistant capability supports controlled change from requirements and source through CI/CD, dependency tracking, impact-aware reporting and exploitation readiness.",
    accent: "amber",
  },
];

const ANNEX = [
  {
    n: "A1",
    title: "Cluster 4 — Circular advanced materials, manufacturing & critical raw materials",
    body: "Best fit where consortia need trusted evidence for advanced materials, material substitution, circular design, specialist surfaces, glass, manufacturing change, quality, longevity, sustainability and adoption by industrial buyers.",
  },
  {
    n: "A2",
    title: "Cluster 4 / Cluster 6 — Textile circularity, construction products & built-environment materials",
    body: "Best fit where fragmented supply chains, SMEs, recyclers, contractors and assessors need to prove circularity, reuse, repair, recycling, embodied impact, missing-data gaps and realised outcomes to larger corporates or public bodies.",
  },
  {
    n: "A3",
    title: "Cluster 6 — Bioeconomy, biomanufacturing, microbiome & complex recycling",
    body: "Best fit where projects need governed evidence across feedstocks, process validation, bio-based innovation, complex plastics, food-system microbiome solutions, regulated claims, local data custody and exploitation readiness.",
  },
];

// Plain-text summary used by the "Copy summary" button.
const SUMMARY_TEXT = [
  `KATLAS — Horizon Europe fit: governed coordination for multi-party innovation`,
  ``,
  SUBHEAD,
  ``,
  ...CARDS.map((c) => `${c.n} ${c.title}\n${c.body}`),
  ``,
  `Distributed custody. Governed coordination. Trusted reporting.`,
  ``,
  `Custody controls the evidence. Authority governs the action. Receipts prove what happened.`,
  ``,
  `Annex — 3 best-fit Horizon programme areas`,
  ...ANNEX.map((a) => `\n${a.n} ${a.title}\n${a.body}`),
  ``,
  `https://platform.katlastechnology.com/fit/horizon`,
].join("\n");

// ---------------------------------------------------------------- ui

const Card = ({ n, title, body, accent }) => (
  <article
    data-testid={`horizon-card-${n.toLowerCase()}`}
    className="bg-[#020B14] border border-white/[0.08] p-5 sm:p-6"
  >
    <div className="flex items-baseline gap-3 mb-2">
      <span
        className={`mono text-[10px] uppercase tracking-[0.22em] ${
          accent === "amber" ? "text-[#EAB308]" : "text-[#0066FF]"
        }`}
      >
        {n}
      </span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
    <h3 className="font-display text-[17px] sm:text-lg text-white leading-snug">
      {title}
    </h3>
    <p className="mt-2.5 text-sm text-slate-300 leading-relaxed">{body}</p>
  </article>
);

const AnnexCard = ({ n, title, body }) => (
  <article
    data-testid={`horizon-annex-${n.toLowerCase()}`}
    className="bg-[#020B14] border border-white/[0.08] p-5 sm:p-6"
  >
    <div className="flex items-baseline gap-3 mb-2">
      <span className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
        ANNEX {n}
      </span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
    <h4 className="font-display text-[15px] sm:text-base text-white leading-snug">
      {title}
    </h4>
    <p className="mt-2.5 text-sm text-slate-300 leading-relaxed">{body}</p>
  </article>
);

// ---------------------------------------------------------------- page

export default function HorizonFit() {
  const [annexOpen, setAnnexOpen] = useState(false);
  const shareUrl = "https://platform.katlastechnology.com/fit/horizon";

  return (
    <div className="min-h-screen bg-[#020B14] text-white" id="top">
      <Nav />
      <main className="pt-24 pb-24">
        <div className="max-w-[720px] mx-auto px-5 sm:px-8">
          {/* Breadcrumb + meta */}
          <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-8 flex-wrap">
            <Link to="/" data-testid="crumb-home" className="hover:text-white">
              Platform
            </Link>
            <span>/</span>
            <Link to="/fit" data-testid="crumb-fit" className="hover:text-white">
              Calls / Challenges Fit
            </Link>
            <span>/</span>
            <span className="text-slate-200">Horizon Europe</span>
            <span className="ml-auto text-slate-500">≈ 2 min read</span>
          </div>

          {/* Hero */}
          <header className="mb-10">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
              Branch · Horizon Europe
            </div>
            <h1
              data-testid="horizon-headline"
              className="font-display text-[28px] sm:text-4xl mt-4 leading-[1.15]"
            >
              {HEADLINE}
            </h1>
            <p
              data-testid="horizon-subhead"
              className="mt-5 text-[15px] sm:text-base text-slate-300 leading-relaxed"
            >
              {SUBHEAD}
            </p>

            <div
              data-testid="horizon-strap"
              className="mt-6 inline-flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border border-white/[0.08] bg-white/[0.02] px-3 py-2"
            >
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Consortium
              </span>
              <span className="text-slate-700" aria-hidden>·</span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Evidence
              </span>
              <span className="text-slate-700" aria-hidden>·</span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Bounded AI
              </span>
              <span className="text-slate-700" aria-hidden>·</span>
              <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Receipts
              </span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
              <CopyButton
                text={SUMMARY_TEXT}
                label="Copy summary"
                testId="copy-summary-btn"
              />
              <CopyButton
                text={shareUrl}
                label="Copy share link"
                testId="copy-share-btn"
              />
            </div>
          </header>

          {/* Main cards */}
          <section
            data-testid="horizon-cards"
            className="grid grid-cols-1 gap-3.5"
          >
            {CARDS.map((c) => (
              <Card key={c.n} {...c} />
            ))}
          </section>

          {/* Callout */}
          <section className="mt-10 border border-[#0066FF]/30 bg-[#0066FF]/[0.06] p-5 sm:p-6">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
              In one line
            </div>
            <p
              data-testid="horizon-callout"
              className="mt-3 font-display text-lg sm:text-xl text-white leading-snug"
            >
              Distributed custody.
              <br className="sm:hidden" /> Governed coordination.
              <br className="sm:hidden" />{" "}
              <span className="text-[#EAB308]">Trusted reporting.</span>
            </p>
          </section>

          {/* CAR footer line */}
          <section className="mt-8 border-t border-white/[0.08] pt-6">
            <p
              data-testid="horizon-car-line"
              className="text-sm sm:text-base text-slate-300 leading-relaxed"
            >
              <span className="text-[#0066FF]">Custody</span> controls the
              evidence.{" "}
              <span className="text-[#0066FF]">Authority</span> governs the
              action.{" "}
              <span className="text-[#EAB308]">Receipts</span> prove what
              happened.
            </p>
          </section>

          {/* Collapsible annex */}
          <section className="mt-10">
            <button
              type="button"
              data-testid="annex-toggle"
              aria-expanded={annexOpen}
              onClick={() => setAnnexOpen((v) => !v)}
              className="w-full flex items-center justify-between border border-white/[0.12] hover:border-white/30 bg-[#071627] hover:bg-[#0D2035] px-5 py-4 transition-colors"
            >
              <span className="text-left">
                <span className="block mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
                  Annex
                </span>
                <span className="block font-display text-base sm:text-lg text-white mt-1">
                  3 best-fit Horizon programme areas
                </span>
              </span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={`text-slate-300 transition-transform ${
                  annexOpen ? "rotate-180" : ""
                }`}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {annexOpen && (
              <div
                data-testid="annex-body"
                className="mt-3 grid grid-cols-1 gap-3.5"
              >
                {ANNEX.map((a) => (
                  <AnnexCard key={a.n} {...a} />
                ))}
              </div>
            )}
          </section>

          {/* Gated Horizon Cases — first case: Cluster 5 Battery Materials */}
          <GatedHorizonCases />

          {/* Bottom navigation */}
          <nav className="mt-12 pt-6 border-t border-white/[0.08] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <Link
              to="/fit"
              data-testid="back-fit-link"
              className="mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-white inline-flex items-center gap-2"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to Calls / Challenges Fit
            </Link>
            <Link
              to="/fit/esrc-future-data-services"
              data-testid="next-esrc-link"
              className="mono text-[11px] uppercase tracking-[0.2em] text-[#0066FF] hover:text-white inline-flex items-center gap-2"
            >
              ESRC Future Data Services branch
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
            </Link>
          </nav>
        </div>
      </main>
      <Footer />
    </div>
  );
}
